import { ChangeDetectionStrategy, Component, forwardRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { AppConfig } from 'src/app/app-config.loader';
import { GassClaims } from 'src/app/auth/gass-claims';
import { HierarchyService } from 'src/app/core/hierarchy.service';
import { PosizioniService } from 'src/app/core/posizioni.service';
import { GestionePosizioniItem } from 'src/app/features/gestione-posizioni/gestione-posizioni.model';
import { Posizione } from '../models/posizione.model';
import { AddPositionComponent } from './add-position/add-position.component';

@Component({
  selector: 'app-position-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <div class="grid">
    @for (level of hierarchy; track $index) {
      @if ($index === hierarchyDescriptionIndex) {
        <div class="col-12 lg:col-6" [class.error]="posizionecomplete.invalid && (posizionecomplete.dirty || posizionecomplete.touched)">
        <div class="row-folder">
          <div>
            <label class="small-label" [for]="'autocomplete_' + $index">{{level}}
              <i class="fas fa-exclamation-circle" title="Selezionare un fascicolo"></i>
            </label>
            <p-autoComplete [id]="'autocomplete_' + $index" [suggestions]="autocompleteValues()[$index]" [delay]="20" [size]="50" [appendTo]="'body'"
                (completeMethod)="autocomplete($event, $index)" (onSelect)="selectItem($event.value, $index)" (onClear)="clearItem($index)" [tabindex]="$index+1"
                [dropdown]="true" dataKey="posizioneId" [forceSelection]="true" field="title" [autoHighlight]="true" [autofocus]="true"
                [(ngModel)]="posizioni()[$index]" [disabled]="fullyDisabled() || !enabledStatus()[$index]" [inputStyle]="{'width': '420px'}" [required]="true" #posizionecomplete="ngModel">
            </p-autoComplete>
          </div>
          @if (allowCreation()) {
          <div class="add-button" *hasClaim="GassClaims.AnagFascicoli">
            <button class="addButton"pButton type="button" icon="fa fa-plus" label="" [disabled]="!posizioni()[$index-1]?.posizioneId && $index !== 0"
            (click)="openModalAddFolder(posizioni()[$index-1], $index)"></button>
          </div>
          }
        </div>
        </div>
        <div class="col-12 lg:col-6">
          <p>{{posizioni()[$index]?.descrizione}}</p>
        </div>
      }
      @else {
      <div class="col-12 lg:col-6">
          <div class="row-folder">
            <div>
              <label class="small-label" [for]="'autocomplete_' + $index">{{level}}</label>
              <p-autoComplete [id]="'autocomplete_' + $index" [suggestions]="autocompleteValues()[$index]" [delay]="20" [size]="50" [appendTo]="'body'"
                (completeMethod)="autocomplete($event, $index)" (onSelect)="selectItem($event.value, $index)" (onClear)="clearItem($index)" [tabindex]="$index+1"
                [dropdown]="true" dataKey="posizioneId" [forceSelection]="true" field="title" [autoHighlight]="true"
                [(ngModel)]="posizioni()[$index]" [disabled]="!enabledStatus()[$index]" [inputStyle]="{'width': '420px'}">
              </p-autoComplete>
            </div>
            @if (allowCreation()) {
              <div class="add-button" *hasClaim="GassClaims.AnagFascicoli">
                <button class="addButton" pButton type="button" icon="fa fa-plus" label="" [disabled]="!posizioni()[$index-1]?.posizioneId && $index !== 0"
                (click)="openModalAddFolder(posizioni()[$index-1], $index - 1)"></button>
              </div>
            }
          </div>
      </div>
      }
    }

    @if (allowCreation()) {
    <app-add-position [visible]="addPositionVisible()" [nodeToModify]="father()" typeOfEvent="add"
      (isCompleted)="addedPosition($event)" (closed)="addPositionClosed()" />
    }
  </div>
  `,
  styles: [`
    i.fa-exclamation-circle { display: none; }
    .error i.fa-exclamation-circle { display: inline-block; font-size: 20px; color: #b94a48; margin-left: 5px; }
    .error > label { color: #b94a48; }
    .row-folder { display: inline-flex; }
    .row-folder > .add-button { display: flex; align-items: flex-end; }
    .addButton  {margin-left: 8px; }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PositionSelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PositionSelectComponent),
      multi: true
    }
  ]
})

export class PositionSelectComponent implements OnInit, ControlValueAccessor, Validator {
  addPosition = viewChild.required<AddPositionComponent>(AddPositionComponent);
  fullyDisabled = input<boolean>(false);
  noExpired = input<boolean>(false);
  allowCreation = input<boolean>(false);

  public posizioni = signal<(Posizione | undefined)[]>([]);
  public hierarchy!: string[];
  public hierarchyDescriptionIndex!: number;
  public autocompleteValues = signal<Posizione[][]>([]);
  public enabledStatus = signal<boolean[]>([]);
  public GassClaims = GassClaims;
  public addPositionVisible = signal<boolean>(false);
  public father = signal<GestionePosizioniItem | null>(null);

  private onChangeFn!: (_: (Posizione | undefined)[]) => void;
  private onTouchedFn!: () => void;

  private appConfig = inject(AppConfig);
  private hierarchyService = inject(HierarchyService);
  private posizioniService = inject(PosizioniService);

  ngOnInit(): void {
    this.hierarchy = this.hierarchyService.getHierarchyLabels();
    this.hierarchyDescriptionIndex = this.hierarchyService.getDescriptionIndex();
    this.autocompleteValues.set(new Array<Posizione[]>(this.hierarchy.length).fill([]));
    this.enabledStatus.set(new Array<boolean>(this.hierarchy.length).fill(false));
    if (typeof this.posizioni() === 'undefined' || this.posizioni() === null) {
      this.posizioni.set(new Array<Posizione>(this.hierarchy.length));
    }
  }

  writeValue(value: Posizione[]): void {
    if (typeof value === 'undefined' || value === null || value.length === 0) {
      this.posizioni.set(new Array<Posizione>(this.hierarchy.length));
      this.onChangeFn?.(this.posizioni());
    } else {
      this.posizioni.set(this.mapPosizioni(value));
    }

    this.enableDropdowns();
  }

  registerOnChange(fn: (_: unknown) => void): void { this.onChangeFn = fn; }
  registerOnTouched(fn: () => void): void { this.onTouchedFn = fn; }

  validate(): ValidationErrors | null {
    return (!this.posizioni() || this.posizioni()[this.hierarchyDescriptionIndex]) ? null : { invalid: true };
  }

  autocomplete(event: { query: string }, level: number): void {
    const value = event.query.toLowerCase();
    const parent = this.getParent(level);
    const searchLimit = this.appConfig.isSottoFascicolo(level) || this.appConfig.isSottoServizioInterno(level) ? 0 : 20;
    if (parent) {
      this.posizioniService.getAutocompleteForPath(parent, value, searchLimit).subscribe(f => {
        this.processAutocompleteResult(f, level);
      });
    } else {
      this.posizioniService.getAutocompleteForLevel(level, value, searchLimit).subscribe(f => {
        this.processAutocompleteResult(f, level);
      });
    }
  }

  private processAutocompleteResult(f: Posizione[], level: number) {
    let posizioni = this.mapPosizioni(f);
    posizioni = this.escludiChiusi(posizioni);
    posizioni = this.noExpired() ? this.escludiScaduti(posizioni) : posizioni;
    this.autocompleteValues.update(a => a.map((oldValues, i) => i === level ? posizioni : oldValues));
  }

  private escludiScaduti(p: Posizione[]) {
    const currentDate = new Date();
    return p.filter(ep => ep.dataChiusura == null || new Date(ep.dataChiusura) > currentDate)
  }

  selectItem(value: Posizione, level: number): void {
    this.posizioni()[level] = value;
    this.resetChildren(level);
    const childLevel = level + 1;
    if (childLevel < this.hierarchy.length) {
      this.posizioniService.getAutocompleteForPath(value.posizioneId, '', 0).subscribe(f => {
        let posizioni = this.mapPosizioni(f);
        posizioni = this.escludiChiusi(posizioni);
        this.posizioni.update(posizioni => posizioni.filter((_, i) => i !== childLevel));
        this.enabledStatus.update(s => s.map((status, i) => i === childLevel ? posizioni.length > 0 : status));
      });
    }

    if (level > 0 && level === this.hierarchyDescriptionIndex) {
      // Popola i parent a partire dal fascicolo
      this.posizioniService.getAncestor(value.posizioneId).subscribe(ancestors => {
        this.posizioni.set(this.mapPosizioni([...ancestors]));
        this.enabledStatus.update(s => s.map((_, i) => i < ancestors.length - 1));
      });
    }

    this.onChangeFn(this.posizioni());
    this.onTouchedFn();
  }

  clearItem(level: number): void {
    this.resetChildren(level);
    this.posizioni.update(posizioni => posizioni.filter((p, i) => i !== level));
    this.onChangeFn(this.posizioni());
  }

  clearForm(): void {
    this.resetChildren(0);
    setTimeout(() => {
      const autocompleteElement = document.getElementById('autocomplete_0');
      if (!autocompleteElement) return;

      autocompleteElement.classList.remove('ng-invalid', 'ng-touched', 'ng-dirty');
      const divinError = autocompleteElement.parentElement;
      divinError?.classList.remove('error');
      const rootDiv = autocompleteElement.parentElement?.parentElement?.parentElement;
      rootDiv?.classList.remove('ng-invalid', 'ng-touched', 'ng-dirty');
    });
  }

  private enableDropdowns() {
    this.enabledStatus.update(s => s.map((_, level) => level === 0 || level === this.hierarchyDescriptionIndex || !!this.posizioni()[level] || !!this.posizioni()[level - 1]));
  }

  private resetChildren(parentLevel: number) {
    let posizioni = this.posizioni();
    const status = this.enabledStatus();
    for (let level = parentLevel + 1; level < this.hierarchy.length; level++) {
      posizioni = posizioni.filter((_, i) => i !== level);
      status[level] = false;
    }

    this.posizioni.set(posizioni);
    this.enabledStatus.set(status);
  }

  private getParent(level: number) {
    return this.posizioni()[--level] ? this.posizioni()[level]?.posizioneId : null;
  }

  private mapPosizioni(posizioni: Posizione[]) {
    return posizioni.map(p => {
      if (p.sigla && p.descrizione) {
        p.title = p.descrizione ? `${p.sigla} - ${p.descrizione.substring(0, 100)}` : `${p.sigla}`;
      } else if (p.sigla) {
        p.title = p.sigla;
      } else if (p.descrizione) {
        p.title = p.descrizione.substring(0, 100);
      }

      return p;
    });
  }

  private escludiChiusi(posizioni: Posizione[]) {
    if (window.location.pathname === '/nuovo') {
      return posizioni.filter(node => node.chiuso === false);
    } else {
      return posizioni;
    }
  }

  public openModalAddFolder(posizione: Posizione | undefined, curPos: number): void {
    const posizioneToModify = posizione ? { ...posizione, level: curPos } as GestionePosizioniItem : null;
    this.father.set(posizioneToModify);
    this.addPositionVisible.set(true);
  }

  addPositionClosed(): void {
    this.addPositionVisible.set(false);
  }

  addedPosition(e: Posizione): void {
    const currentPosition = e.posizioneId.split('/').length - 3;
    if (currentPosition < 0) return;

    let wkPosizioni = this.posizioni();
    const wkStatus = this.enabledStatus();
    wkStatus[currentPosition] = true;
    for (let i = 0; i < this.enabledStatus.length; i++) {
      if (i > currentPosition) {
        wkStatus[i] = false;
        wkPosizioni = wkPosizioni.filter((_, index) => index !== i);
      }
    }

    wkPosizioni[currentPosition] = {
      ...e,
      title: `${e.sigla}${(e.descrizione == null ? '' : ' - ' + e.descrizione)}`
    };

    this.posizioni.set(wkPosizioni);
    this.enabledStatus.set(wkStatus);
    this.onChangeFn(this.posizioni());
    this.onTouchedFn();
  }
}
